import { createSelector, createSlice } from '@reduxjs/toolkit';
import { getNotifications, getNotificationCount } from '../../api/home';

export const NotificationMetricType = {
  OrdersSubmitted: 'ordersSubmitted',
  OrdersNeedApproval: 'ordersNeedApproval',
  InvoicesSubmitted: 'invoicesSubmitted',
  InvoicesNeedApproval: 'invoicesNeedApproval',
  RentalsSubmitted: 'rentalsSubmitted',
  RentalsNeedApproval: 'rentalsNeedApproval',
  TicketsUnread: 'ticketsUnread',
};

export const notificationMetricListSelectors = {
  [NotificationMetricType.OrdersSubmitted]: (data) => data?.orderMetrics?.submittedAwaitingApprovalOrders,
  [NotificationMetricType.OrdersNeedApproval]: (data) => data?.orderMetrics?.awaitingApprovalOrders,
  [NotificationMetricType.InvoicesSubmitted]: (data) => data?.invoiceMetrics?.submittedAwaitingApprovalInvoices,
  [NotificationMetricType.InvoicesNeedApproval]: (data) => data?.invoiceMetrics?.awaitingApprovalInvoices,
  [NotificationMetricType.RentalsSubmitted]: (data) => data?.rentalMetrics?.submittedAwaitingApprovalRentals,
  [NotificationMetricType.RentalsNeedApproval]: (data) => data?.rentalMetrics?.needsApprovalRentals,
  [NotificationMetricType.TicketsUnread]: (data) => data?.ticketMetrics?.respondedUnreadTickets,
};

const PENDING_TTL_MS = 60 * 1000;

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const createEmptyPending = () => Object.fromEntries(
  Object.values(NotificationMetricType).map((type) => [type, {}])
);

const initialState = {
  isLoading: false,
  error: null,
  notificationsData: null,
  hasLoaded: false,
  pendingRemovals: createEmptyPending(),
  pendingAdds: createEmptyPending(),
  lastNotificationCount: null,
  hasNewNotifications: false,
};

const prunePending = (pending, isConfirmed, now) => {
  Object.keys(pending).forEach((id) => {
    if (isConfirmed(id) || now - pending[id] > PENDING_TTL_MS) delete pending[id];
  });
};

const preparePending = (type, ids) => ({
  payload: {
    type,
    ids: (Array.isArray(ids) ? ids : [ids]).map(String),
    at: Date.now(),
  },
});

export const applyPendingToList = (list, removed, added) => {
  const items = list.filter((item) => !hasOwn(removed, item.id));
  const itemIds = new Set(items.map((item) => String(item.id)));
  const addedIds = Object.keys(added).filter((id) => !itemIds.has(id));
  return { items, count: items.length + addedIds.length, ids: [...itemIds, ...addedIds] };
};

export const selectAdjustedMetrics = createSelector(
  [
    (state) => state.notifications.notificationsData,
    (state) => state.notifications.pendingRemovals,
    (state) => state.notifications.pendingAdds,
  ],
  (data, removed, added) => Object.fromEntries(
    Object.entries(notificationMetricListSelectors).map(([type, selectList]) => [
      type,
      applyPendingToList(selectList(data) ?? [], removed[type], added[type]),
    ])
  )
);

const slice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      state.notificationsData = state.notificationsData ?? {};
    },

    getNotificationsSuccess: {
      reducer(state, action) {
        const { data, receivedAt } = action.payload;
        state.isLoading = false;
        state.error = null;
        state.hasLoaded = true;
        state.notificationsData = data;
        Object.entries(notificationMetricListSelectors).forEach(([type, selectList]) => {
          const serverIds = new Set((selectList(data) ?? []).map((item) => String(item.id)));
          prunePending(state.pendingRemovals[type], (id) => !serverIds.has(id), receivedAt);
          prunePending(state.pendingAdds[type], (id) => serverIds.has(id), receivedAt);
        });
      },
      prepare(data) {
        return { payload: { data, receivedAt: Date.now() } };
      },
    },

    markMetricRemoved: {
      reducer(state, action) {
        const { type, ids, at } = action.payload;
        if (!hasOwn(state.pendingRemovals, type)) return;
        ids.forEach((id) => {
          delete state.pendingAdds[type][id];
          state.pendingRemovals[type][id] = at;
        });
      },
      prepare: preparePending,
    },

    markMetricAdded: {
      reducer(state, action) {
        const { type, ids, at } = action.payload;
        if (!hasOwn(state.pendingAdds, type)) return;
        ids.forEach((id) => {
          delete state.pendingRemovals[type][id];
          state.pendingAdds[type][id] = at;
        });
      },
      prepare: preparePending,
    },

    setLastNotificationCount(state, action) {
      state.lastNotificationCount = action.payload;
    },

    setHasNewNotifications(state, action) {
      state.hasNewNotifications = action.payload;
    },
  },
});

export default slice.reducer;

export const { markMetricAdded, markMetricRemoved, setHasNewNotifications } = slice.actions;

export function fetchNotifications() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getNotifications();
      dispatch(slice.actions.getNotificationsSuccess(response?.data ?? {}));
    } catch (error) {
      if (typeof error === 'string' && error.startsWith('Request canceled')) return;
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function markMetricAddedIfCounted(type, sourceId, newId) {
  return (dispatch, getState) => {
    if (newId == null) return;
    const list = notificationMetricListSelectors[type]?.(getState().notifications.notificationsData) ?? [];
    if (list.some((item) => String(item.id) === String(sourceId))) dispatch(markMetricAdded(type, newId));
  };
}

export function pollNotificationCount() {
  return async (dispatch, getState) => {
    const response = await getNotificationCount().catch(() => null);
    const count = response?.data;
    if (count === undefined) return;
    const { lastNotificationCount } = getState().notifications;
    if (lastNotificationCount !== null && count !== lastNotificationCount) {
      if (count > lastNotificationCount) dispatch(slice.actions.setHasNewNotifications(true));
      dispatch(fetchNotifications());
    }
    dispatch(slice.actions.setLastNotificationCount(count));
  };
}
